import moment from 'moment'

/**
 * 保留指定位数小数
 * @param {*} number 数字
 * @param {*} n 保留小数位数
 * @returns 转换后的字符串
 */
export function numberRound(number, n) {
  if (n > 20 || n < 0) throw new RangeError('toFixed() digits argument must be between 0 and 20')
  const isNegative = number < 0
  if (isNaN(number) || number >= Math.pow(10, 21)) return number.toString()
  if (typeof n == 'undefined' || n == 0) return Math.round(number).toString()

  let result = number.toString().replace('-', '')
  const [integer, decimal] = result.split('.')
  // 整数的情况
  if (!decimal) {
    result += '.'
    for (let i = 0; i < n; i++) result += '0'
    return isNegative ? `-${result}` : result
  }

  if (decimal.length == n) return isNegative ? `-${result}` : result
  if (decimal.length < n) {
    for (let i = 0; i < n - decimal.length; i++) result += '0'
    return isNegative ? `-${result}` : result
  }
  result = integer + '.' + decimal.substr(0, n)
  const last = decimal.substr(n, 1)
  // 四舍五入，转换为整数再处理，避免浮点数精度的损失
  if (parseInt(last, 10) >= 5) {
    const x = Math.pow(10, n)
    result = (Math.round(parseFloat(result) * x) + 1) / x
    result = result.toFixed(n)
  }
  return isNegative ? `-${result}` : result
}

/**
 * 判断数据是否为空
 * @param {any} v 数据
 * @returns Boolean
 */
const isEmpty = (v) => Number.isNaN(v) || v === undefined || v === null || v === ''

/**
 * 获取传入参数的数据类型
 * @param {any} o 原始数据
 * @returns string 数据类型
 */
export const getDataType = (o, getText = false) => {
  let typeStr = (Object.prototype.toString.call(o).match(/\[object (.*?)\]/) || [])[1]
  if (typeStr === 'Number' && !isFinite(o)) typeStr = isNaN(o) ? 'NaN' : 'Infinity'
  const types = {
    Null: 'Null',
    Undefined: 'Undefined',
    Object: '对象',
    Array: '数组',
    String: '字符串',
    Number: '数字',
    Boolean: '布尔',
    Function: '方法',
    RegExp: '正则',
    NaN: 'NaN',
    Infinity: 'Infinity',
    Date: '日期'
  }
  return getText ? types[typeStr] ?? typeStr : typeStr
}

/**
 * 强制将值转换为数字
 * @param {any} v 要转换为数字的值
 * @returns 数字
 */
export const convertNumber = (v) => {
  if (isEmpty(v)) return 0
  if (v === 'null' || v === 'undefined') v = 0
  if (typeof v === 'boolean') v = +v
  if (typeof v !== 'number') v = Number(v)
  return v
}

/**
 * 强制将值转换为字符串
 * @param {any} v 要转换为数字的值
 * @returns 数字
 */
export const convertString = (v) => {
  if (isEmpty(v)) return ''
  return v + ''
}

/**
 * 获取日期/时间格式
 * @param {String} v 日期字符串
 * @returns 当前日期的格式
 */
export const getDatePattern = (v) => {
  let formats = {
    'HH:mm:ss': /^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/,
    'HH:mm': /^(?:[01]\d|2[0-3]):[0-5]\d$/,
    'YYYY-MM-DD': /^\d{1,4}(-)(1[0-2]|0?[1-9])\1(0?[1-9]|[1-2]\d|30|31)$/,
    'YYYY-MM': /^\d{1,4}(-)(1[0-2]|0?[1-9])$/,
    'YYYY-MM-DD HH:mm:ss':
      /^\d{4}([/:-\S])(1[0-2]|0?[1-9])\1(0?[1-9]|[1-2]\d|30|31) (?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/,
    'YYYY-MM-DD HH:mm': /^\d{4}([/:-\S])(1[0-2]|0?[1-9])\1(0?[1-9]|[1-2]\d|30|31) (?:[01]\d|2[0-3]):[0-5]\d$/
  }
  for (let k in formats) if (formats[k].test(v)) return k
  return ''
}

/**
 * 转换日期
 * @param {String|Number} v 日期或时间戳
 * @param {String} format 转换结果格式
 * @param {String} pattern 传入的日期字符串格式
 * @returns 转换结果
 */
export const formatDate = (v, format, pattern) => {
  const momentInstance = pattern ? moment(v, pattern) : moment(v)
  let result = ''
  switch (format) {
    case 'MOMENT':
      result = momentInstance
      break
    case 'WEEK':
      result = [7, 1, 2, 3, 4, 5, 6][momentInstance.day()]
      break

    case 'TIMESTAMP':
      result = momentInstance.valueOf()
      break

    default:
      result = momentInstance.format(format)
      break
  }
  return result
}

/**
 * 转换日期
 * @param {String|Number} v 时间戳或格式时间
 * @param {*} format YYYY\MM\DD\HH\mm\ss
 * @returns 格式化后的时间
 */
export const convertDate = (v, format) => {
  if (isEmpty(v)) return format === 'MOMENT' ? moment() : ''
  let returnText = '',
    vFormat = getDatePattern(v)
  // 时间戳
  if (typeof v === 'number' || v * 1 == v) returnText = formatDate(v * 1000, format)
  else if (vFormat !== '') returnText = formatDate(v, format, vFormat)
  else returnText = formatDate(v, format)
  return returnText
}

/**
 * 计算两个日期的时间差
 * @param {Number|String} start 开始日期
 * @param {Number|String} end 结束如期
 * @param {String} format 要转换成的格式 "y"-年；"M"-月；"d"-天；"h"-小时；"m"-分钟；“s”-秒钟；如果不指定这个参数，则默认为"d"
 * @returns Number 计算结果
 */
export const calcDateDifference = (start, end, format) => {
  start = start * 1 == start ? moment(start * 1000) : moment(start, getDatePattern(start))
  end = end * 1 == end ? moment(end * 1000) : moment(end, getDatePattern(end))
  return Math.abs(start.diff(end, format))
}

export const getMoment = () => moment
